import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { switchMap, take, filter } from 'rxjs/operators';
import { IResponseSuccess } from '@core/interfaces';
import { Store as StoreModel, DeliveryZone } from '@core/models';
import { Store } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { selectStore, selectStoreList } from '@store/selectors';
import { forkJoin, of } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class DeliveryZoneService {
	store$: Observable<StoreModel | null>;

	constructor(
		private http: HttpClient,
		private store: Store<AppState>
	) {
		this.store$ = this.store.select(selectStore);
	}

	findAll(localChannelId: string | null): Observable<IResponseSuccess<DeliveryZone>> {
		return this.store$.pipe(
			filter((store): store is StoreModel => !!store),
			filter(() => !!localChannelId),
			switchMap(store => {
				return this.http.get<IResponseSuccess<DeliveryZone>>(`${environment.microConfiguraciones}/zonaentrega`, {
					params: {
						localId: store.idLocal,
						useLocalCanalId: localChannelId as string,
					},
					headers: {
						authorization: store.apiKey,
					},
				});
			}),
			take(1)
		);
	}

	getAllDeliveryZone(stores: StoreModel[]): Observable<DeliveryZone[]> {
		const requests = stores.map(store => this.findAll(store.idLocal));

		return forkJoin(requests).pipe(
			switchMap((responses: IResponseSuccess<DeliveryZone>[]) => {
				const allZones = responses.reduce((acc, res) => acc.concat(res.data), [] as DeliveryZone[]);
				return [allZones];
			})
		);
	}

	getAllDeliveryZoneFromState(): Observable<DeliveryZone[]> {
		return this.store.select(selectStoreList).pipe(
			switchMap(stores => {
				if (!stores) {
					return of([]);
				}
				const requests = stores.map(store => {
					return this.http.get<IResponseSuccess<DeliveryZone>>(`${environment.microConfiguraciones}/zonaentrega`, {
						params: {
							localId: store.idLocal,
						},
						headers: {
							authorization: store.apiKey,
						},
					});
				});
				return forkJoin(requests);
			}),
			switchMap((responses: IResponseSuccess<DeliveryZone>[]) => {
				const allZones = responses.reduce((acc, res) => acc.concat(res.data), [] as DeliveryZone[]);
				return [allZones];
			})
		);
	}

	storeWithDelivery(deliveryZones: DeliveryZone[]): Observable<StoreModel[]> {
		return this.store.select(selectStoreList).pipe(
			switchMap(stores => {
				if (!stores || !deliveryZones) return of([]);

				// Extraer localId únicos de las delivery zones
				const uniqueLocalIds = [...new Set(deliveryZones.map(zone => zone.localId))];

				// Buscar las tiendas correspondientes
				const storesWithDelivery = stores.filter(store => uniqueLocalIds.includes(store.idLocal));

				return [storesWithDelivery];
			})
		);
	}
}
